import React, { useRef, useState, useEffect } from 'react';
import { createMemoStyles, defaultFonts, safeRest } from '@wds/utils';
import { IconInfoFilled, IconWarningFilled, IconError, IconCheck, IconLock } from '@wds/icons';
import { Slide } from '@wds/motion';
import { CloseButton } from '@wds/internal-components';
import PropTypes from 'prop-types';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var _assign = function __assign() {
  _assign = Object.assign || function __assign(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) {
        if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
      }
    }
    return t;
  };
  return _assign.apply(this, arguments);
};
function __rest(s, e) {
  var t = {};
  for (var p in s) {
    if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  }
  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
}
var useStyles$3 = createMemoStyles(function (theme) {
  var palette = theme.palette,
    type = theme.type;
  var toast = {
    typeColor: palette.text.light,
    titleFontSize: type.fontSize.body
  };
  var toastStyles = {
    wrenchToastTitle: function wrenchToastTitle() {
      return {
        color: toast.typeColor,
        fontSize: toast.titleFontSize
        // marginLeft: color && spacing[6],
      };
    }
  };

  return toastStyles;
});
var ToastTitle = function ToastTitle(_a) {
  var children = _a.children,
    rest = __rest(_a, ["children"]);
  var wrenchToastTitle = useStyles$3().wrenchToastTitle;
  return React.createElement("div", _assign({
    className: wrenchToastTitle
  }, rest), children);
};
ToastTitle.propTypes = {
  children: PropTypes.node.isRequired
};
var useStyles$2 = createMemoStyles(function (theme) {
  var palette = theme.palette,
    type = theme.type,
    spacing = theme.spacing;
  var toast = {
    typeColor: palette.text.light,
    bodyTypeSize: type.fontSize.bodySm,
    linkColor: palette.link.contrast
  };
  var toastStyles = {
    wrenchToastBody: function wrenchToastBody() {
      return {
        color: toast.typeColor,
        fontSize: toast.bodyTypeSize,
        marginTop: spacing[4],
        // marginLeft: color && spacing[6],
        '& > a': {
          color: toast.linkColor,
          textDecoration: 'underline'
        }
      };
    }
  };
  return toastStyles;
});
var ToastBody = function ToastBody(_a) {
  var children = _a.children,
    rest = __rest(_a, ["children"]);
  var wrenchToastBody = useStyles$2().wrenchToastBody;
  return React.createElement("div", _assign({
    className: wrenchToastBody
  }, rest), children);
};
ToastBody.propTypes = {
  children: PropTypes.node.isRequired
};
var useStyles$1 = createMemoStyles(function (theme) {
  var spacing = theme.spacing;
  var toastStyles = {
    wrenchToastFooter: function wrenchToastFooter() {
      return {
        marginTop: spacing[4],
        // marginLeft: color && spacing[6],
        '& > *': {
          marginRight: spacing[3]
        }
      };
    }
  };
  return toastStyles;
});
var ToastFooter = function ToastFooter(_a) {
  var children = _a.children,
    rest = __rest(_a, ["children"]);
  var wrenchToastFooter = useStyles$1().wrenchToastFooter;
  return React.createElement("div", _assign({
    className: wrenchToastFooter
  }, rest), children);
};
ToastFooter.propTypes = {
  children: PropTypes.node.isRequired
};
var useStyles = createMemoStyles(function (theme) {
  var _a;
  var palette = theme.palette,
    radius = theme.radius,
    shadow = theme.shadow,
    type = theme.type,
    spacing = theme.spacing,
    iconSize = theme.iconSize,
    breakpoints = theme.breakpoints;
  var toast = {
    padding: spacing[5],
    bgColor: palette.background.dark,
    typeColor: palette.text.light,
    borderRadius: radius.small,
    width: 350,
    titleFontSize: type.fontSize.body,
    bodyTypeSize: type.fontSize.bodySm,
    linkColor: palette.link.contrast,
    boxShadow: shadow.none
  };
  var toastStyles = {
    wrenchToastContainer: (_a = {
      position: 'fixed',
      right: spacing[3],
      bottom: spacing[3],
      left: spacing[3],
      zIndex: function zIndex(_a) {
        var elevation = _a.elevation;
        return elevation !== null && elevation !== void 0 ? elevation : theme.elevation.space;
      }
    }, _a["@media (min-width:".concat(breakpoints.sm, "px)")] = {
      right: spacing[5],
      bottom: spacing[5],
      width: toast.width,
      left: 'auto'
    }, _a),
    wrenchToast: function wrenchToast() {
      return _assign(_assign({}, defaultFonts(theme)), {
        boxSizing: 'border-box',
        margin: 0,
        padding: toast.padding,
        backgroundColor: toast.bgColor,
        color: toast.typeColor,
        borderRadius: toast.borderRadius,
        boxShadow: toast.boxShadow
      });
    },
    wrenchToastIcon: function wrenchToastIcon() {
      return {
        fontSize: iconSize.md,
        marginRight: spacing[3],
        float: 'left',
        display: 'inline-flex'
      };
    },
    wrenchToastContent: function wrenchToastContent() {
      return {
        display: 'inline-flex'
      };
    }
  };
  return toastStyles;
});
var TIMEOUT_DELAY = 3000;
var Toast = function Toast(_a) {
  var children = _a.children,
    _b = _a.color,
    color = _b === void 0 ? undefined : _b,
    _c = _a.onClose,
    onClose = _c === void 0 ? undefined : _c,
    _d = _a.autoClose,
    autoClose = _d === void 0 ? true : _d,
    _e = _a.dismissible,
    dismissible = _e === void 0 ? false : _e,
    _f = _a.show,
    show = _f === void 0 ? true : _f,
    _g = _a.elevation,
    elevation = _g === void 0 ? undefined : _g,
    _h = _a.ariaLabel,
    ariaLabel = _h === void 0 ? 'Close Toast' : _h,
    rest = __rest(_a, ["children", "color", "onClose", "autoClose", "dismissible", "show", "elevation", "ariaLabel"]);
  var timeoutRef = useRef({
    timeout: undefined
  });
  var _j = useState(show),
    localShow = _j[0],
    setLocalShow = _j[1];
  var _k = useStyles({
      color: color,
      elevation: elevation
    }),
    wrenchToastContainer = _k.wrenchToastContainer,
    wrenchToast = _k.wrenchToast,
    wrenchToastIcon = _k.wrenchToastIcon,
    wrenchToastContent = _k.wrenchToastContent;
  var ToastIcon = color ? {
    info: IconInfoFilled,
    warning: IconWarningFilled,
    error: IconError,
    success: IconCheck,
    upgrade: IconLock
  }[color] : IconInfoFilled;
  var handleClose = function handleClose() {
    if (timeoutRef.current.timeout) {
      clearTimeout(timeoutRef.current.timeout);
    }
    setLocalShow(false);
    if (onClose) {
      onClose();
    }
  };
  var handleMouseEnter = function handleMouseEnter() {
    if (autoClose && timeoutRef.current.timeout) {
      clearTimeout(timeoutRef.current.timeout);
    }
  };
  var handleMouseExit = function handleMouseExit() {
    if (autoClose) {
      timeoutRef.current.timeout = setTimeout(function () {
        return handleClose();
      }, TIMEOUT_DELAY);
    }
  };
  useEffect(function () {
    if (autoClose && show) {
      timeoutRef.current.timeout = setTimeout(function () {
        return handleClose();
      }, TIMEOUT_DELAY);
    } else if (timeoutRef.current.timeout) {
      clearTimeout(timeoutRef.current.timeout);
    }
    setLocalShow(show);
    var timeEffectRef = timeoutRef.current.timeout;
    return function () {
      if (timeEffectRef) {
        clearTimeout(timeEffectRef);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);
  return React.createElement("div", {
    className: wrenchToastContainer
  }, React.createElement(Slide, {
    in: localShow,
    distance: "far"
  }, React.createElement("div", null, React.createElement("div", _assign({
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseExit,
    className: wrenchToast,
    role: "alert"
  }, safeRest(rest)), dismissible && React.createElement(CloseButton, {
    onClick: handleClose,
    label: ariaLabel,
    variant: "light"
  }), React.createElement("div", {
    className: wrenchToastContent
  }, color && React.createElement("div", {
    className: wrenchToastIcon
  }, React.createElement(ToastIcon, {
    color: color,
    onDark: true,
    title: "".concat(color, " icon.")
  })), React.createElement("div", null, children))))));
};
Toast.Title = ToastTitle;
Toast.Body = ToastBody;
Toast.Footer = ToastFooter;
var TOAST_COLORS = ['success', 'warning', 'error', 'info', 'upgrade'];
export { TOAST_COLORS, Toast };